import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { useSim } from "../../lib/store";

// Rising / advancing flood plane + foam for offshore (tsunami) quakes in the 1:1 view.
export function WaterPlane() {
  const waterRef = useRef();
  const foamRef = useRef();
  const offshore = useSim((s) => s.epicenter?.offshore);

  const foamGeo = useMemo(() => {
    const N = 400;
    const pos = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 70;
      pos[i * 3 + 1] = 0;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 70;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }, []);

  useFrame((state) => {
    const st = useSim.getState();
    const active = st.epicenter?.offshore && st.simulating;
    const w = waterRef.current, f = foamRef.current;
    if (!w) return;
    if (!active) {
      w.visible = false;
      if (f) f.visible = false;
      window.__flood = { active: false, level: -999, edge: -999, dirX: 0, dirZ: 0 };
      return;
    }
    w.visible = true;
    if (f) f.visible = true;
    const t = state.clock.elapsedTime;
    const magF = THREE.MathUtils.clamp((st.magnitude - 4) / 5.5, 0, 1);
    const maxLevel = 1.2 + magF * 3.2; // higher magnitude floods deeper
    const level = -1.5 + st.simProgress * (maxLevel + 1.5);
    w.position.y = level + Math.sin(t * 1.6) * 0.06; // gentle swell
    // advance the leading edge across the city
    w.position.z = THREE.MathUtils.lerp(-24, 0, Math.min(1, st.simProgress * 1.4));
    if (f) f.position.y = level + 0.05;
    // publish flood state so cars & rubble can float / drift with the current
    window.__flood = {
      active: true,
      level: Math.max(0, level),
      edge: w.position.z + 45,
      dirX: 0.8,
      dirZ: 0.6,
      strength: 1.2 + magF * 2.4,
    };
  });

  if (!offshore) return null;

  return (
    <group>
      <mesh ref={waterRef} rotation={[-Math.PI / 2, 0, 0]} visible={false}>
        <planeGeometry args={[90, 90, 1, 1]} />
        <meshStandardMaterial color="#1d5f86" transparent opacity={0.62} metalness={0.6} roughness={0.25} emissive="#0a2f4a" emissiveIntensity={0.4} side={THREE.DoubleSide} />
      </mesh>
      <points ref={foamRef} geometry={foamGeo} visible={false}>
        <pointsMaterial color="#cfeeff" size={0.5} sizeAttenuation transparent opacity={0.7} depthWrite={false} />
      </points>
    </group>
  );
}
